import { getMeetingsFeedTopPx } from 'shared/utils/meetings_feed_top'

export type DateScrollAnchors = {
  header: HTMLElement | null
  firstMeeting: HTMLElement | null
  lastMeeting: HTMLElement | null
}

export type ScrollDirection = 'down' | 'up' | 'none'

const isBrowserEnvironment = (): boolean => typeof window !== 'undefined'

function isInFeed(rect: DOMRect, feedTop: number, viewportBottom: number): boolean {
  return rect.height > 0 && rect.bottom > feedTop && rect.top < viewportBottom
}

/**
 * The next day's label/header is still visible in the meeting table (not fully tucked
 * under the preset row). Used to defer advancing the date picker when scrolling down.
 */
function isNextDayStillPending(
  anchor: DateScrollAnchors,
  feedTop: number,
  viewportBottom: number,
): boolean {
  if (anchor.header) {
    const rect = anchor.header.getBoundingClientRect()
    return rect.height > 0 && rect.bottom > feedTop
  }

  if (anchor.firstMeeting) {
    const rect = anchor.firstMeeting.getBoundingClientRect()
    return rect.height > 0 && rect.top > feedTop && rect.top < viewportBottom
  }

  return false
}

/**
 * Scrolling down: stay on date[i] until date[i + 1]'s header has fully scrolled under
 * the preset row (bottom <= feedTop). Matches picking a date — the header hides under
 * preset while that day's first meeting sits on the feed-top line.
 */
export function computeScrollDownAnchorDate(
  anchors: Map<string, DateScrollAnchors>,
  sortedDates: string[],
  feedTop: number,
  viewportBottom: number,
): string | null {
  if (sortedDates.length === 0) return null

  let activeDate = sortedDates[0]

  for (let i = 0; i < sortedDates.length - 1; i += 1) {
    const nextAnchor = anchors.get(sortedDates[i + 1])
    if (nextAnchor && isNextDayStillPending(nextAnchor, feedTop, viewportBottom)) {
      return sortedDates[i]
    }
    activeDate = sortedDates[i + 1]
  }

  return activeDate
}

/**
 * Scrolling up: switch to the previous date only once its last meeting (or only meeting) is
 * visible in the meeting table below the sticky chrome — not when the date header peeks in.
 */
export function computeScrollUpAnchorDate(
  anchors: Map<string, DateScrollAnchors>,
  sortedDates: string[],
  feedTop: number,
  viewportBottom: number,
): string | null {
  let activeDate: string | null = null
  let activeTop = Infinity

  sortedDates.forEach((rawDate) => {
    const anchor = anchors.get(rawDate)
    if (!anchor?.lastMeeting) return

    const rect = anchor.lastMeeting.getBoundingClientRect()
    if (!isInFeed(rect, feedTop, viewportBottom)) return

    if (rect.top < activeTop) {
      activeTop = rect.top
      activeDate = rawDate
    }
  })

  return activeDate
}

export function computeScrollAnchorDate(
  anchors: Map<string, DateScrollAnchors>,
  sortedDates: string[],
  scrollDirection: ScrollDirection,
  feedTop = getMeetingsFeedTopPx(),
  viewportBottom = isBrowserEnvironment() ? window.innerHeight : 800,
): string | null {
  if (anchors.size === 0 || sortedDates.length === 0) return null

  if (scrollDirection === 'up') {
    return computeScrollUpAnchorDate(anchors, sortedDates, feedTop, viewportBottom)
  }

  return computeScrollDownAnchorDate(anchors, sortedDates, feedTop, viewportBottom)
}
